
import React from 'react';
import { Product } from '../../../types';
import { Plus, ArrowRight, Zap } from 'lucide-react';
import { StoreImage } from '../../ui/StoreImage';

interface CardProps {
  product: Product & { boostAmount?: number };
  onAddToCart: (product: Product) => void;
}

export const CardOverlay: React.FC<CardProps> = ({ product, onAddToCart }) => {
  const hasVariants = product.variants && product.variants.length > 0;
  
  let displayPrice = product.price;
  if (hasVariants) {
      const prices = product.variants!.map(v => v.price);
      displayPrice = Math.min(...prices);
  }

  const displayImage = product.images?.[0] || product.imageUrl;

  return (
    <div 
        onClick={() => onAddToCart(product)} 
        className="group relative w-full rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-1 transform-gpu shadow-card hover:shadow-xl"
        style={{ 
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            backgroundColor: '#F9F9F9',
            aspectRatio: '3/4' // Force aspect ratio to prevent collapse
        }}
    >
        {/* Background Image with Zoom Effect */}
        <div className="absolute inset-0 bg-[#F9F9F9]">
            {displayImage ? (
                <StoreImage
                    src={displayImage}
                    width={640}
                    alt={product.name} 
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" 
                />
            ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-300 bg-stone-100">Bild saknas</div>
            )}
        </div>

        {/* Top Badges */}
        <div className="absolute top-4 left-4 right-4 z-20 flex justify-between items-start pointer-events-none">
            {/* Boost Badge */}
            {product.boostAmount && product.boostAmount > 0 ? (
                <div className="bg-red-500 text-white px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest shadow-lg flex items-center gap-1 animate-pulse-slow backdrop-blur-md bg-opacity-90">
                    <Zap size={10} fill="currentColor" />
                    +{product.boostAmount}kr
                </div>
            ) : <div></div>}

            {/* Type/News Badge */}
            <div className="bg-[#2D2D2D] text-white px-3 py-1 rounded-full text-[10px] font-bold tracking-wide shadow-sm backdrop-blur-sm bg-opacity-90">
                {hasVariants ? 'Välj sort' : 'Nyhet'}
            </div>
        </div>

        {/* Glassmorphism Information Panel */}
        <div className="absolute inset-x-3 bottom-3 z-10">
            <div 
                className="rounded-xl p-4 flex flex-col gap-1 transition-all duration-300 backdrop-blur-md border border-white/40 shadow-sm group-hover:bg-white/90"
                style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.85)',
                }}
            >
                <div className="flex justify-between items-start">
                    <div className="flex-1 pr-2 min-w-0">
                        <h3 className="text-base font-sans font-bold leading-tight mb-1 truncate text-[#2D2D2D]">
                            {product.name}
                        </h3>
                        <p className="text-xs font-medium opacity-70 line-clamp-1 text-[#2D2D2D]">
                            {product.description}
                        </p>
                    </div>
                    
                    {/* Floating Price */}
                    <span className="text-sm font-bold whitespace-nowrap text-[#816358]">
                        {displayPrice} kr
                    </span>
                </div>

                {/* Action Button - Always visible on mobile (touch), sliding on desktop */}
                <div className="md:h-0 md:overflow-hidden md:group-hover:h-10 md:opacity-0 md:group-hover:opacity-100 transition-all duration-300 ease-out mt-3 md:mt-0 md:group-hover:mt-3">
                    <button className="w-full h-10 bg-[#2D2D2D] text-white rounded-lg text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-black transition-colors shadow-lg">
                        {hasVariants ? 'Konfigurera' : 'Lägg i varukorg'} <ArrowRight size={14} />
                    </button>
                </div>
            </div>
        </div>
    </div>
  );
};
